/**
 * TrackKit public API — window.trackkit
 *
 * A small, framework-agnostic surface for product analytics + lightweight experiments,
 * built on top of the existing telemetry pipeline (custom events flow through the same
 * batch sender → ingest-v2 → telemetry_events). Unlike rrweb.record.addCustomEvent, this
 * works even if rrweb never loads, because it writes directly to the telemetry buffer.
 *
 *   window.trackkit.track("trial_started", { experiment: "x", variant: "treatment" })
 *   const v = window.trackkit.variant("homepage-hero", ["control", "treatment"])
 */

import { win, sessionState } from './state';
import type { TelemetryEvent } from './types';
import { redactUrl } from './redact';

/**
 * Deliver a single custom event immediately via its own keepalive POST. This does NOT rely
 * on the rrweb batch pipeline (which only flushes after >=12 events or on session end), so
 * exposures/conversions land even for low-activity or quick-bounce visitors, and even if
 * session recording is skipped entirely.
 */
function sendCustom(event: string, data: Record<string, any>): void {
  const tel: TelemetryEvent = { type: 'custom', subtype: event, timestamp: Date.now(), data: data || {} };
  const nowIso = new Date().toISOString();
  const payload = {
    sessionId: sessionState.sessionId,
    projectId: sessionState.projectId,
    visitorId: sessionState.visitorId,
    url: redactUrl(window.location.href),
    userAgent: navigator.userAgent,
    deviceType: sessionState.isMobile ? 'mobile' : 'desktop',
    timestamp: nowIso,
    duration: 0,
    errorCount: 0,
    networkErrorCount: 0,
    events: [],
    startTime: new Date(sessionState.sessionStartTime || Date.now()).toISOString(),
    endTime: nowIso,
    eventCount: 0,
    isFinalBatch: false,
    telemetry: [tel],
  };
  fetch(sessionState.backendUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(function () {});
}

/** Install window.trackkit. Idempotent; safe to call before or independent of rrweb. */
export function installTrackkit(): void {
  if (win.trackkit) return;

  const track = (event: string, props: Record<string, any> = {}): void => {
    if (!event) return;
    try {
      sendCustom(event, props);
    } catch (_e) {
      /* never let analytics throw into the host app */
    }
  };

  const variant = (experimentId: string, variants: string[]): string => {
    const fallback = (variants && variants[0]) || 'control';
    if (!experimentId || !variants || variants.length === 0) return fallback;
    const key = 'tk_exp_' + experimentId;
    let assigned: string | null = null;
    try {
      assigned = localStorage.getItem(key);
    } catch (_e) {
      /* storage blocked */
    }
    if (!assigned || variants.indexOf(assigned) === -1) {
      let idx: number;
      try {
        idx = Math.floor((crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296) * variants.length);
      } catch (_e) {
        idx = Math.floor(Math.random() * variants.length);
      }
      assigned = variants[idx] || fallback;
      try {
        localStorage.setItem(key, assigned);
      } catch (_e) {
        /* storage blocked — assignment won't be sticky, still usable this load */
      }
    }
    // Emit one exposure per session per experiment (denominator for the test).
    let firstThisSession = true;
    try {
      const sk = 'tk_exp_seen_' + experimentId;
      firstThisSession = !sessionStorage.getItem(sk);
      if (firstThisSession) sessionStorage.setItem(sk, '1');
    } catch (_e) {
      /* fall through — emit anyway */
    }
    if (firstThisSession) track('experiment_exposure', { experiment: experimentId, variant: assigned });
    return assigned;
  };

  win.trackkit = { track, variant };
}
